import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, MapPin, FlaskConical, Image, MessageSquare } from 'lucide-react';
import { request, fmtDate, fmtNum, pointUrl } from '@/lib/api';
import { RefreshButton } from '@/components/RefreshButton';

interface Dashboard {
  users: { total: number; verified: number; pro: number; new_7d: number; new_30d: number };
  points: {
    total: number;
    public: number;
    investigated: number;
    not_investigated: number;
    errored: number;
    new_7d: number;
  };
  content: { images: number; comments: number; resources: number };
  research: { queued: number; running: number; done: number; error: number };
  feedbacks: { new: number; reviewed: number; resolved: number };
  categories: { category: string; count: number }[];
  communities: { name: string; total: number; investigated: number; errored: number }[];
  models: { ai: string | null; model: string | null; count: number }[];
  recentUsers: { id: string; email: string; full_name: string | null; created_at: string }[];
  recentPoints: {
    id: string;
    title: string | null;
    category: string;
    share_code: string | null;
    created_at: string;
    user_email: string;
  }[];
}

function StatCard({
  icon: Icon,
  title,
  value,
  details,
  progress,
}: {
  icon: typeof Users;
  title: string;
  value: number;
  details: [string, number][];
  // Barra de progreso opcional (p. ej. puntos procesados sobre el total).
  progress?: { value: number; total: number; label: string };
}) {
  const pct =
    progress && progress.total > 0 ? (progress.value / progress.total) * 100 : 0;
  return (
    <div className="rounded-xl bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-stone-500">
        <Icon size={16} />
        <span className="text-sm">{title}</span>
      </div>
      <p className="mt-2 text-3xl font-semibold">{fmtNum(value)}</p>
      <ul className="mt-2 space-y-0.5 text-xs text-stone-500">
        {details.map(([label, n]) => (
          <li key={label}>
            <span className="font-medium text-stone-700">{fmtNum(n)}</span> {label}
          </li>
        ))}
      </ul>
      {progress && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-stone-500">
            <span>{progress.label}</span>
            <span className="font-medium tabular-nums text-stone-700">
              {fmtNum(Math.round(pct))}%
            </span>
          </div>
          <div className="mt-1 h-2 rounded bg-stone-100">
            <div className="h-2 rounded bg-amber-600" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState(false);
  // Incrementar reloadKey vuelve a disparar el fetch (botón de refrescar).
  const [reloadKey, setReloadKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    setError(false);
    setRefreshing(true);
    request<Dashboard>('/backoffice/dashboard')
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setRefreshing(false));
  }, [reloadKey]);

  const refresh = () => setReloadKey((k) => k + 1);

  if (error) return <p className="text-sm text-red-600">{t('common.error')}</p>;
  if (!data) return <p className="text-sm text-stone-500">{t('common.loading')}</p>;

  const researchTotal =
    data.research.queued + data.research.running + data.research.done + data.research.error;

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">{t('dashboard.title')}</h1>
        <RefreshButton onClick={refresh} spinning={refreshing} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Users}
          title={t('dashboard.users')}
          value={data.users.total}
          details={[
            [t('dashboard.usersNew7d'), data.users.new_7d],
            [t('dashboard.usersNew30d'), data.users.new_30d],
            [t('dashboard.usersVerified'), data.users.verified],
            [t('dashboard.usersPro'), data.users.pro],
          ]}
        />
        <StatCard
          icon={MapPin}
          title={t('dashboard.points')}
          value={data.points.total}
          details={[
            [t('dashboard.pointsNew7d'), data.points.new_7d],
            [t('dashboard.pointsPublic'), data.points.public],
            [t('dashboard.pointsInvestigated'), data.points.investigated],
            [t('dashboard.pointsNotInvestigated'), data.points.not_investigated],
            [t('dashboard.pointsError'), data.points.errored],
          ]}
          progress={{
            value: data.points.investigated + data.points.errored,
            total: data.points.total,
            label: t('dashboard.pointsProcessed'),
          }}
        />
        <StatCard
          icon={FlaskConical}
          title={t('dashboard.research')}
          value={researchTotal}
          details={[
            [t('dashboard.researchQueued'), data.research.queued],
            [t('dashboard.researchRunning'), data.research.running],
            [t('dashboard.researchDone'), data.research.done],
            [t('dashboard.researchError'), data.research.error],
          ]}
        />
        <StatCard
          icon={Image}
          title={t('dashboard.content')}
          value={data.content.images}
          details={[
            [t('dashboard.images'), data.content.images],
            [t('dashboard.comments'), data.content.comments],
            [t('dashboard.resources'), data.content.resources],
            [
              `${t('dashboard.feedback')} (${t('dashboard.feedbackNew')})`,
              data.feedbacks.new,
            ],
          ]}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="text-sm font-medium text-stone-500">{t('dashboard.byCategory')}</h2>
          <ul className="mt-3 space-y-2">
            {data.categories.map((c) => {
              const max = data.categories[0]?.count || 1;
              return (
                <li key={c.category} className="text-sm">
                  <div className="flex justify-between">
                    <span>{t(`categories.${c.category}`, c.category)}</span>
                    <span className="font-medium">{fmtNum(c.count)}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded bg-stone-100">
                    <div
                      className="h-1.5 rounded bg-amber-600"
                      style={{ width: `${(c.count / max) * 100}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="text-sm font-medium text-stone-500">{t('dashboard.recentUsers')}</h2>
          <ul className="mt-3 divide-y divide-stone-100">
            {data.recentUsers.map((u) => (
              <li key={u.id} className="py-2 text-sm">
                <p className="truncate font-medium">{u.full_name || u.email}</p>
                <p className="truncate text-xs text-stone-500">
                  {u.email} · {fmtDate(u.created_at)}
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="text-sm font-medium text-stone-500">{t('dashboard.recentPoints')}</h2>
          <ul className="mt-3 divide-y divide-stone-100">
            {data.recentPoints.map((p) => {
              const url = pointUrl(p.share_code);
              const title = p.title || t('dashboard.noTitle');
              return (
                <li key={p.id} className="py-2 text-sm">
                  <p className="truncate font-medium">
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-amber-700 hover:underline"
                      >
                        {title}
                      </a>
                    ) : (
                      title
                    )}
                  </p>
                  <p className="truncate text-xs text-stone-500">
                    {t(`categories.${p.category}`, p.category)} · {p.user_email} ·{' '}
                    {fmtDate(p.created_at)}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="mt-6 rounded-xl bg-white p-4 shadow-sm">
        <h2 className="text-sm font-medium text-stone-500">{t('dashboard.byCommunity')}</h2>
        {data.communities.length === 0 ? (
          <p className="mt-3 text-sm text-stone-400">{t('common.empty')}</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-stone-500">
                  <th className="pb-2 font-medium">{t('dashboard.community')}</th>
                  <th className="pb-2 text-right font-medium">{t('dashboard.communityTotal')}</th>
                  <th className="pb-2 text-right font-medium">
                    {t('dashboard.communityInvestigated')}
                  </th>
                  <th className="pb-2 text-right font-medium">
                    {t('dashboard.communityError')}
                  </th>
                  <th className="pb-2 text-right font-medium">
                    {t('dashboard.communityPending')}
                  </th>
                  <th className="pb-2 pl-4 font-medium">{t('dashboard.communityProgress')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {data.communities.map((c) => {
                  // Los puntos con error se consideran procesados (la IA ya los
                  // intentó), así que cuentan para el progreso junto a los investigados.
                  const processed = c.investigated + c.errored;
                  const pct = c.total > 0 ? (processed / c.total) * 100 : 0;
                  const pending = c.total - processed;
                  return (
                    <tr key={c.name}>
                      <td className="py-2 pr-2">{c.name}</td>
                      <td className="py-2 text-right tabular-nums">{fmtNum(c.total)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtNum(c.investigated)}</td>
                      <td
                        className={`py-2 text-right tabular-nums ${
                          c.errored > 0 ? 'font-medium text-amber-600' : 'text-stone-400'
                        }`}
                      >
                        {fmtNum(c.errored)}
                      </td>
                      <td
                        className={`py-2 text-right tabular-nums ${
                          pending > 0 ? 'font-medium text-red-600' : 'text-stone-400'
                        }`}
                      >
                        {fmtNum(pending)}
                      </td>
                      <td className="py-2 pl-4">
                        <div className="flex items-center gap-2">
                          <div className="h-2 flex-1 rounded bg-stone-100">
                            <div
                              className="h-2 rounded bg-amber-600"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="w-10 shrink-0 text-right text-xs tabular-nums text-stone-500">
                            {fmtNum(Math.round(pct))}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-xl bg-white p-4 shadow-sm">
        <h2 className="text-sm font-medium text-stone-500">{t('dashboard.byModel')}</h2>
        {data.models.length === 0 ? (
          <p className="mt-3 text-sm text-stone-400">{t('common.empty')}</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {data.models.map((m) => {
              const max = data.models[0]?.count || 1;
              const label = m.model ?? t('dashboard.modelUnknown');
              return (
                <li key={`${m.ai ?? ''}-${m.model ?? ''}`} className="text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="min-w-0 truncate font-mono text-xs">
                      {label}
                      {m.ai && <span className="ml-1 font-sans text-stone-400">· {m.ai}</span>}
                    </span>
                    <span className="shrink-0 font-medium">{fmtNum(m.count)}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded bg-stone-100">
                    <div
                      className="h-1.5 rounded bg-amber-600"
                      style={{ width: `${(m.count / max) * 100}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="mt-4 inline-flex items-center gap-1 text-xs text-stone-400">
        <MessageSquare size={12} />
        {t('dashboard.feedback')}: {data.feedbacks.new} {t('dashboard.feedbackNew')} ·{' '}
        {data.feedbacks.reviewed} {t('dashboard.feedbackReviewed')} · {data.feedbacks.resolved}{' '}
        {t('dashboard.feedbackResolved')}
      </p>
    </div>
  );
}
